import React, { useRef, useState } from 'react';
import { ArrowRight, Mail, Linkedin, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/apiClient';
import TurnstileWidget from './TurnstileWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Footer = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    revenue: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const idempotencyKeyRef = useRef(crypto.randomUUID());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) {
      setError('Conferma di aver letto la Privacy Policy per inviare il modulo.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiFetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKeyRef.current,
        },
        body: JSON.stringify({ ...formData, turnstileToken, privacyAccepted }),
      });
      
      if (!response.ok) {
        throw new Error('Errore durante l\'invio della richiesta');
      }
      
      setSubmitted(true);
      setFormData({ name: '', email: '', company: '', revenue: '', message: '' });
      setPrivacyAccepted(false);
      setTurnstileToken('');
      idempotencyKeyRef.current = crypto.randomUUID();
    } catch (err) {
      setError('Si è verificato un errore. Riprova più tardi.');
      console.error('Contact form error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer id="contact" className="bg-[#070b34] relative overflow-hidden">
      {/* CTA Section */}
      <div className="relative">
        
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-start">
            {/* Left - Text */}
            <div>
              <p className="text-[#d4883a] font-semibold mb-3 sm:mb-4 tracking-wide text-sm sm:text-base">Lavoriamo insieme</p>
              <h2 
                className="text-3xl sm:text-4xl lg:text-6xl text-white font-bold leading-tight mb-4 sm:mb-6"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Pronto a{' '}
                <span className="text-[#d4883a]">dominare</span>
                <br className="hidden sm:block" /><span className="sm:hidden"> </span>il tuo mercato?
              </h2>
              
              <p className="text-white/60 text-base sm:text-lg mb-6 sm:mb-10 leading-relaxed">
                Compila il form per verificare se la tua azienda è idonea per una collaborazione. Rispondo personalmente entro 48 ore.
              </p>

              {/* Requirements - Hidden on small mobile */}
              <div className="space-y-4 mb-6 sm:mb-10 hidden sm:block">
                <p className="text-white/40 text-sm font-medium tracking-wide">Requisiti minimi:</p>
                <div className="space-y-3">
                  {[
                    'Fatturato annuo superiore a €100.000',
                    'Ambizione di crescita concreta e misurabile',
                    'Disponibilità a investire in strategia',
                    'Apertura al cambiamento e all\'innovazione',
                  ].map((req, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-[#d4883a] flex-shrink-0" />
                      <span className="text-white/70 text-sm sm:text-base">{req}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Info */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a 
                  href="mailto:info@nicolascerni.it"
                  className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm sm:text-base"
                >
                  <Mail className="w-5 h-5" />
                  info@nicolascerni.it
                </a>
                <a 
                  href="https://www.linkedin.com/in/nicolascerni/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm sm:text-base"
                >
                  <Linkedin className="w-5 h-5" />
                  LinkedIn
                </a>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10 border border-white/10">
              {submitted ? (
                <div className="text-center py-8 sm:py-12">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#d4883a]/20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                    <CheckCircle className="w-7 h-7 sm:w-8 sm:h-8 text-[#d4883a]" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Richiesta Inviata</h3>
                  <p className="text-white/60 text-sm sm:text-base">Ti risponderò personalmente entro 48 ore per valutare se possiamo lavorare insieme.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                  <input
                    type="text"
                    name="website"
                    value={formData.website || ''}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                    <div>
                      <label className="block text-white/60 text-xs sm:text-sm mb-2">Nome completo *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full bg-white/5 border border-white/20 text-white px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-base focus:outline-none focus:border-[#d4883a] transition-colors"
                        placeholder="Mario Rossi"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-xs sm:text-sm mb-2">Email aziendale *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full bg-white/5 border border-white/20 text-white px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-base focus:outline-none focus:border-[#d4883a] transition-colors"
                        placeholder="mario@azienda.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                    <div>
                      <label className="block text-white/60 text-xs sm:text-sm mb-2">Azienda *</label>
                      <input
                        type="text"
                        required
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        className="w-full bg-white/5 border border-white/20 text-white px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-base focus:outline-none focus:border-[#d4883a] transition-colors"
                        placeholder="Nome Azienda Srl"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-xs sm:text-sm mb-2">Fatturato annuo *</label>
                      <select
                        required
                        value={formData.revenue}
                        onChange={(e) => setFormData({...formData, revenue: e.target.value})}
                        className="w-full bg-white/5 border border-white/20 text-white px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-base focus:outline-none focus:border-[#d4883a] transition-colors appearance-none"
                      >
                        <option value="" className="bg-[#070b34]">Seleziona...</option>
                        <option value="100-500k" className="bg-[#070b34]">€100K - €500K</option>
                        <option value="500k-1m" className="bg-[#070b34]">€500K - €1M</option>
                        <option value="1m-5m" className="bg-[#070b34]">€1M - €5M</option>
                        <option value="5m+" className="bg-[#070b34]">Oltre €5M</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-white/60 text-xs sm:text-sm mb-2">Cosa vorresti ottenere? *</label>
                    <textarea
                      required
                      rows={3}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full bg-white/5 border border-white/20 text-white px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-base focus:outline-none focus:border-[#d4883a] transition-colors resize-none"
                      placeholder="Descrivi brevemente la tua situazione attuale e gli obiettivi..."
                    />
                  </div>

                  <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-[#d4883a]/10 rounded-xl">
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#d4883a] flex-shrink-0 mt-0.5" />
                    <p className="text-white/60 text-xs sm:text-sm">
                      Accetto solo 4 nuovi clienti alla volta. Se i requisiti sono soddisfatti, ti contatterò per una call conoscitiva gratuita.
                    </p>
                  </div>

                  {/* GDPR Consent */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="privacy-consent"
                      checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-[#d4883a] focus:ring-[#d4883a] focus:ring-offset-0 cursor-pointer"
                    />
                    <label htmlFor="privacy-consent" className="text-white/60 text-xs sm:text-sm cursor-pointer">
                      Ho letto la{' '}
                      <Link to="/privacy-policy" className="text-[#d4883a] hover:underline">
                        Privacy Policy
                      </Link>
                      {' '}per la gestione della mia richiesta. Non è un consenso alla newsletter. *
                    </label>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-xl">
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  <TurnstileWidget action="contact" onVerify={setTurnstileToken} />

                  <button
                    type="submit"
                    disabled={!privacyAccepted || !turnstileToken || loading}
                    className={`w-full font-bold py-3 sm:py-4 rounded-xl transition-all flex items-center justify-center gap-2 text-sm sm:text-base ${
                      privacyAccepted && turnstileToken && !loading
                        ? 'bg-[#d4883a] hover:bg-[#d4896a] text-white cursor-pointer' 
                        : 'bg-white/10 text-white/40 cursor-not-allowed'
                    }`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        Invia Richiesta
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 sm:gap-4">
            <p className="text-white/40 text-xs sm:text-sm text-center md:text-left">
              Copyright © 2011 – {new Date().getFullYear()} • P.Iva 02345430694
            </p>
            <div className="flex items-center gap-4 sm:gap-6">
              <a href="/privacy-policy" className="text-white/40 hover:text-white/60 text-xs sm:text-sm transition-colors">Privacy Policy</a>
              <a href="/cookie-policy" className="text-white/40 hover:text-white/60 text-xs sm:text-sm transition-colors">Cookie Policy</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
